import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'utils/translation';
import { useModal } from '../../../../components/v2/Modal/useModal';
import { useModalContent } from '../../../../components/v2/Modal/useModalContent';
import Spacer from '../../../../components/v2/Spacer/Spacer';
import {
  mapFieldToProps,
  mapValuesToPayload,
} from '../../../../modules/intelligent-layer/utils';
import { useCreateAssetSeriesSpec, useCreateDeviceSeries } from '../hooks';
import Typography from 'components/v2/Typography/Typography';
import Section from 'components/v2/Section/Section';
import Button, { ButtonGroup } from 'components/v2/Button/Button';
import Stack from '@mui/material/Stack/Stack';
import { refetchActiveQueries } from '../../../../utils/QueryClientProvider';
import FluentForm from '../../../../components/v2/Form/FluentForm';
import Form from '../../../../components/v2/Form/Form';
import { FieldType } from '../../../../components/v2/Form/types';
import { Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useNewDeviceFields } from '../../../../modules/intelligent-layer/hooks';
import Loader from '../../../../components/v2/Loader';
import { useShallowEqualEffect } from '../../../../utils/use-shallow-effect';
import http from '../../../../utils/http';
import AccountQuotaGuard from '../../../../modules/users/components/AccountQuotaGuard';

const SuccessNotice = ({
  onClose,
  data,
}: {
  onClose(): void;
  onCreateNext(): void;
  data: {
    deviceType: {
      id: number;
      name: string;
    };
    items: {
      identifier: string;
      status: 'ok' | 'error';
      message: string;
    }[];
  };
}) => {
  const { t } = useTranslation();
  const success = data.items.filter((item) => item.status === 'ok');
  const errors = data.items.filter((item) => item.status !== 'ok');
  const firstSuccess = success[0] ?? null;

  return (
    <Section>
      <Stack direction="column">
        <Typography>
          <Trans
            i18nKey="assets.add_series.devices_added_info"
            values={{
              assetsCount: success.length,
              deviceTypeLabel: data.deviceType.name,
            }}
            components={{ strong: <strong /> }}
          />
          <br />
          {firstSuccess && (
            <Trans
              i18nKey="assets.add_series.id_starting_from_info"
              values={{ identifier: firstSuccess.identifier }}
              components={{ strong: <strong /> }}
            />
          )}
        </Typography>
        <Spacer size={4} />
        {errors.length > 0 && (
          <>
            <strong>
              <Trans
                i18nKey="assets.add_series.errors_count_info"
                values={{ count: errors.length }}
                components={{ strong: <strong /> }}
              />
            </strong>
            <Spacer size={2} />
            <Typography color="error">
              {errors.map((item) => item.identifier).join(', ')}
            </Typography>
            <Spacer size={4} />
          </>
        )}
        <ButtonGroup>
          <Button onClick={onClose}>{t('Close')}</Button>
        </ButtonGroup>
      </Stack>
    </Section>
  );
};

type Props = {
  onClose(): void;
  onSuccess?(): void;
  defaultType?: { label: string; value: number } | null;
  allowTypeChange?: boolean;
};

const MinimumPossibleNumberWatcher = ({ id, query }: any) => {
  const { watch, setValue } = useFormContext();
  const prefix = watch('prefix');

  const handleStartFromCheck = useCallback(() => {
    http
      .get<
        never,
        { startFrom: number; prefix: string }
      >(`/api/device/add-series/${id}`, { params: { prefix } })
      .then((response) => {
        setValue('startFrom', response.startFrom);
      });
  }, [prefix]);

  const checkPrefix = useCallback(() => {
    http
      .get<never, { prefix: string }>(`/api/device/add-series/${id}`)
      .then((response) => {
        if (!prefix && response.prefix) setValue('prefix', response.prefix);
      });
  }, [id, prefix]);

  useShallowEqualEffect(() => {
    void handleStartFromCheck();
  }, [handleStartFromCheck]);

  useShallowEqualEffect(() => {
    void checkPrefix();
  }, [id]);

  return null;
};

const AddManyDevicesForm = ({
  onClose,
  onSuccess,
  defaultType = null,
  allowTypeChange = true,
}: Props) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<any | null>(defaultType);
  const createSeriesSpecQuery = useCreateAssetSeriesSpec(selectedType?.value);
  const { data = [], isLoading } = useNewDeviceFields(selectedType);
  const mutation = useCreateDeviceSeries();
  const modalContent = useModalContent();
  let deviceTypeField = data.find((item) => item.name === 'device_type');

  if (!deviceTypeField) {
    deviceTypeField = data.find((item) => item.name === 'type');
  }

  let deviceTypeProps = null;

  if (deviceTypeField) {
    deviceTypeProps = mapFieldToProps(deviceTypeField);
  }

  const multiDeviceFields = data
    ?.filter((item) => item.name !== 'device_type')
    .filter((item) => item.settings.enabledInSeries);

  useEffect(() => {
    if (mutation.isError) {
      modalContent.scrollToTop();
    }
  }, [mutation.isError]);

  const onSubmit = async (values: any) => {
    try {
      await mutation.mutateAsync(mapValuesToPayload(values));
      onSuccess?.();
      void refetchActiveQueries();
    } catch (error: any) {
      return Promise.reject({
        response: error?.response,
      });
    }
  };

  if (isLoading || createSeriesSpecQuery.isLoading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  return (
    <>
      <Section>
        <Typography variant="h1">{t('assets.add_series_title')}</Typography>
      </Section>
      {mutation.isSuccess ? (
        <SuccessNotice
          data={mutation.data}
          onCreateNext={mutation.reset}
          onClose={onClose}
        />
      ) : (
        <Section padding="horizontal">
          <FluentForm
            onChange={(values) => {
              if (values.hasOwnProperty('device_type')) {
                setSelectedType(values['device_type']);
              }
            }}
            action={onSubmit}
            defaultValues={{
              device_type: selectedType,
              prefix: createSeriesSpecQuery.data?.prefix,
              startFrom: createSeriesSpecQuery.data?.startFrom,
            }}
            renderButtons={false}
          >
            {selectedType && (
              <MinimumPossibleNumberWatcher
                id={selectedType.value}
                query={createSeriesSpecQuery}
              />
            )}
            {allowTypeChange && deviceTypeProps && (
              <Form.Field
                {...deviceTypeProps}
                rules={{ required: true }}
                autoFocus
              />
            )}
            {multiDeviceFields.map((item) => (
              <Form.Field {...mapFieldToProps(item)} />
            ))}
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <Form.Field
                  label={t('assets.add_series_count')}
                  name="assetsCount"
                  type={FieldType.Text}
                  rules={{ required: true }}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={5}>
                <Form.Field
                  label={t('assets.add_series_prefix')}
                  name="prefix"
                  type={FieldType.Text}
                  placeholder=""
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={5}>
                <Form.Field
                  label={t('assets.add_series_start_from')}
                  name="startFrom"
                  type={FieldType.Text}
                  placeholder=""
                  rules={{ required: true }}
                />
              </Grid>
            </Grid>
            <Spacer size={4} />
            <ButtonGroup>
              <Button type="submit" isBusy={mutation.isPending}>
                {t('Add')}
              </Button>
              <Button type="button" variant="text" onClick={onClose}>
                <u>{t('Cancel')}</u>
              </Button>
            </ButtonGroup>
            <Spacer size={2} />
          </FluentForm>
        </Section>
      )}
    </>
  );
};

export const useCreateManyDevicesForm = () => {
  const modal = useModal();

  return (
    props: Omit<
      React.ComponentProps<typeof AddManyDevicesForm>,
      'onClose'
    > = {},
  ) => {
    modal.open(
      ({ close }) => (
        <AccountQuotaGuard quota="maxAssets">
          <AddManyDevicesForm {...props} onClose={close} />
        </AccountQuotaGuard>
      ),
      {
        disableBackdropClose: true,
        size: 'medium',
      },
    );
  };
};
